import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { GetLocation } from "../services/fhir";
import { useSessionContext } from "./SessionContext";

interface LocationContextValue {
  location: fhir5.BundleEntry<fhir5.Location>[] | undefined;
  refetchLocation: Function;
  locationLoading: boolean;
  setIdsOrganizations: Dispatch<SetStateAction<string>>;
}

export const LocationContext = createContext<LocationContextValue>(null!);

export function LocationContextProvider({ children }: { children: ReactNode }) {
  const { access } = useSessionContext();

  const [location, setLocation] =
    useState<fhir5.BundleEntry<fhir5.Location>[]>();
  const [refetch, setRefetch] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [idsOrganizations, setIdsOrganizations] = useState<string>("");
  const lastRequestIdRef = useRef(0);

  function refetchLocation() {
    setRefetch(!refetch);
  }

  useEffect(() => {
    /**
     * Segurança/consistência: ao trocar de organização, não podemos reutilizar
     * `location` anterior (isso pode disparar carregamentos com escopo errado).
     *
     * Também precisamos garantir que `locationLoading` reflita o request real:
     * antes, o `finally` rodava sem aguardar o async, liberando cedo demais.
     *
     * Este guard evita race condition (resposta antiga sobrescrevendo a nova).
     */
    const requestId = ++lastRequestIdRef.current;

    const shouldFetch = Boolean(
      access?.type && (idsOrganizations || access?.organizationId),
    );

    if (!shouldFetch) {
      setLocation(undefined);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLocation(undefined);

    let isActive = true;
    (async () => {
      try {
        const typeSearch =
          access?.type === "patient" && idsOrganizations
            ? { ids: idsOrganizations }
            : { organization: access?.organizationId };

        const locationResponse = await GetLocation(typeSearch);
        if (!isActive || lastRequestIdRef.current !== requestId) return;
        setLocation(locationResponse?.entry);
      } catch (err) {
        if (!isActive || lastRequestIdRef.current !== requestId) return;
        console.log("Location", err);
        setLocation(undefined);
      } finally {
        if (!isActive || lastRequestIdRef.current !== requestId) return;
        setLoading(false);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [access, refetch, idsOrganizations]);

  const contextValue = {
    location,
    refetchLocation,
    locationLoading: loading,
    setIdsOrganizations,
  };

  return (
    <LocationContext.Provider value={contextValue}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationContext() {
  return useContext(LocationContext);
}
